import React, { useCallback, useEffect, useState, useMemo } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
    getTokenFreezeData,
    setStakeModalStatus,
    walletTransactionCheck,
} from "../../store/cabinet/action";
import { parseUnits, isAddress, getAddress, formatUnits } from "viem";
import {
    useAccount,
    useSwitchChain,
    useWriteContract,
    useWaitForTransactionReceipt,
    useReadContract,
} from "wagmi";
import { bsc } from "viem/chains";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

const erc20Abi = [
    {
        type: "function",
        name: "transfer",
        stateMutability: "nonpayable",
        inputs: [
            { name: "to", type: "address" },
            { name: "amount", type: "uint256" },
        ],
        outputs: [{ name: "", type: "bool" }],
    },
];

const erc20MetaAbi = [
    {
        type: "function",
        name: "decimals",
        stateMutability: "view",
        inputs: [],
        outputs: [{ type: "uint8" }],
    },
];

// ✅ balanceOf
const erc20BalanceAbi = [
    {
        type: "function",
        name: "balanceOf",
        stateMutability: "view",
        inputs: [{ name: "owner", type: "address" }],
        outputs: [{ type: "uint256" }],
    },
];

const LaunchpadStakeModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const stakeModalStatus = useSelector((store) => store.cabinet.stakeModalStatus);
    const tokenFreezeData = useSelector((store) => store.cabinet.tokenFreezeData);

    const { writeContractAsync } = useWriteContract();
    const { switchChainAsync } = useSwitchChain();
    const { chainId, isConnected, address } = useAccount();
    const { openConnectModal } = useConnectModal();

    const [amount, setAmount] = useState("");
    const [txHash, setTxHash] = useState(null);
    const [uiError, setUiError] = useState("");
    const [currentPeriodId, setCurrentPeriodId] = useState(false);

    const targetChainId = bsc.id; // 56

    const tokenAddressRaw = tokenFreezeData?.contract_address;
    const recipientRaw = tokenFreezeData?.address_to;

    const tokenAddress = isAddress(tokenAddressRaw || "")
        ? getAddress(tokenAddressRaw)
        : undefined;
    const recipient = isAddress(recipientRaw || "") ? getAddress(recipientRaw) : undefined;

    const { data: decimalsData, isLoading: isDecimalsLoading } = useReadContract({
        address: tokenAddress,
        abi: erc20MetaAbi,
        functionName: "decimals",
        chainId: bsc.id,
        query: { enabled: !!tokenAddress },
    });

    // decimals только когда реально пришли
    const decimals = decimalsData != null ? Number(decimalsData) : null;
    const isDecimalsReady = decimals !== null && Number.isFinite(decimals);

    // ✅ Баланс пользователя
    const {
        data: balanceData,
        isLoading: isBalanceLoading,
        refetch: refetchBalance,
    } = useReadContract({
        address: tokenAddress,
        abi: erc20BalanceAbi,
        functionName: "balanceOf",
        args: address ? [address] : undefined,
        chainId: bsc.id,
        query: { enabled: !!tokenAddress && !!address },
    });

    const balance = typeof balanceData === "bigint" ? balanceData : null;

    const balanceHuman = useMemo(() => {
        if (balance == null || !isDecimalsReady) return null;
        try {
            return formatUnits(balance, decimals);
        } catch {
            return null;
        }
    }, [balance, isDecimalsReady, decimals]);

    // ---- санитайзер инпута
    const onAmountChange = (e) => {
        let v = e.target.value.replace(",", ".");
        v = v.replace(/[^\d.]/g, "");
        const parts = v.split(".");
        if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("").replace(/\./g, "");

        // Ограничиваем дробную часть только когда знаем decimals
        if (isDecimalsReady && parts.length >= 2) {
            const [int, frac = ""] = v.split(".");
            v = int + "." + frac.slice(0, decimals);
        }

        setAmount(v);
    };

    const amountNum = Number(amount);
    const isAmountPositive = !!amount && !Number.isNaN(amountNum) && amountNum > 0;

    // ✅ amountWei из ввода (только когда всё готово)
    const amountWei = useMemo(() => {
        if (!isDecimalsReady || !isAmountPositive) return null;
        try {
            return parseUnits(amount, decimals);
        } catch {
            return null;
        }
    }, [isDecimalsReady, isAmountPositive, amount, decimals]);

    // ✅ Есть ли достаточно баланса
    const hasEnoughBalance = useMemo(() => {
        if (balance == null || amountWei == null) return false;
        return balance >= amountWei;
    }, [balance, amountWei]);

    const { isLoading: isConfirming, isSuccess, error: waitErr } =
        useWaitForTransactionReceipt({
            hash: txHash,
            chainId: targetChainId, // ✅ иногда спасает от “не там ждём”
        });

    const resetForm = useCallback(() => {
        setAmount("");
        setUiError("");
        setTxHash(null);
        setCurrentPeriodId(tokenFreezeData?.periods?.[0]?.id ?? false);
    }, [tokenFreezeData]);

    const closeModal = useCallback(() => {
        resetForm();
        dispatch(setStakeModalStatus(false));
    }, [dispatch, resetForm]);

    // ✅ мягкий анти “вечный pending” тост через 60 сек
    useEffect(() => {
        if (!isConfirming || !txHash) return;
        const id = setTimeout(() => {
            toast.info(t("Транзакция всё ещё подтверждается. Проверьте статус в кошельке/эксплорере."), {
                toastId: `freeze-pending-${txHash}`,
            });
        }, 60_000);
        return () => clearTimeout(id);
    }, [isConfirming, txHash, t]);

    const mainBtnHandler = async () => {
        try {
            setUiError("");

            if (!isConnected) {
                openConnectModal?.();
                return;
            }
            if (!tokenAddress) {
                setUiError("Неверный адрес контракта токена");
                return;
            }
            if (!recipient) {
                setUiError("Неверный адрес получателя");
                return;
            }
            if (!isAmountPositive) {
                setUiError("Введите корректную сумму");
                return;
            }
            if (!isDecimalsReady) {
                setUiError("Не удалось получить данные токена (decimals). Попробуйте ещё раз.");
                return;
            }

            // ✅ баланс/amountWei должны быть готовы
            if (balance == null || amountWei == null) {
                setUiError("Не удалось проверить баланс. Попробуйте ещё раз.");
                return;
            }
            if (!hasEnoughBalance) {
                setUiError(
                    `Недостаточно средств. Доступно: ${balanceHuman ?? "—"} OMT`
                );
                return;
            }

            // гарантируем, что кошелёк в BSC (56)
            if (chainId !== targetChainId) {
                const res = await switchChainAsync({ chainId: targetChainId });
                if (res?.id !== targetChainId) {
                    setUiError("Не удалось переключить сеть на BSC (56)");
                    return;
                }
                // ✅ после свитча можно обновить баланс
                refetchBalance?.();
            }

            const hash = await writeContractAsync({
                address: tokenAddress,
                abi: erc20Abi,
                functionName: "transfer",
                args: [recipient, amountWei],
            });

            setTxHash(hash);
        } catch (e) {
            const rawMsg = String(e?.shortMessage || e?.message || "");
            const lower = rawMsg.toLowerCase();

            // ✅ дружелюбные сообщения на частые кейсы
            if (
                lower.includes("insufficient funds") ||
                lower.includes("transfer amount exceeds balance") ||
                lower.includes("exceeds balance")
            ) {
                setUiError(`Недостаточно средств. Доступно: ${balanceHuman ?? "—"} OMT`);
            } else if (lower.includes("user rejected") || lower.includes("rejected the request")) {
                setUiError("Пользователь отменил транзакцию");
            } else {
                setUiError(rawMsg || "Transaction failed");
            }

            console.error("transfer error:", e);
        }
    };

    useEffect(() => {
        if (tokenFreezeData) {
            setCurrentPeriodId(tokenFreezeData?.periods?.[0]?.id ?? false);
        }
    }, [tokenFreezeData]);

    useEffect(() => {
        if (stakeModalStatus) {
            dispatch(getTokenFreezeData());
        }
    }, [stakeModalStatus, dispatch]);

    // ⬇️ тосты
    useEffect(() => {
        if (uiError) {
            toast.error(uiError, { toastId: "freeze-ui-error" });
        }
    }, [uiError]);

    useEffect(() => {
        if (waitErr) {
            const msg = String(waitErr?.message || waitErr);
            toast.error(msg, { toastId: "freeze-receipt-error" });
        }
    }, [waitErr]);

    useEffect(() => {
        if (isSuccess && txHash) {
            dispatch(
                walletTransactionCheck({
                    hash: txHash,
                    period_id: currentPeriodId,
                    amount,
                })
            );

            toast.success(t("Успех. Ожидайте завершения транзакции!"), {
                toastId: `freeze-success-${txHash}`,
            });

            closeModal();
        }
    }, [isSuccess, txHash, dispatch, currentPeriodId, amount, t, closeModal]);

    // ✅ Блокируем кнопку пока:
    // - нет decimals
    // - грузится баланс
    // - сумма невалидна
    // - баланс меньше суммы
    // - идет подтверждение
    const isButtonDisabled =
        !isAmountPositive ||
        isConfirming ||
        !isDecimalsReady ||
        isDecimalsLoading ||
        isBalanceLoading ||
        !hasEnoughBalance;

    return (
        <Popup
            open={stakeModalStatus}
            modal
            nested
            closeOnDocumentClick={false}
            closeOnEscape={false}
            onClose={closeModal}
        >
            <div className={Styles.modalContainer}>
                <div className={Styles.modalCloseBtn} onClick={closeModal}>
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.modalTitle}>{t("Заморозить")} OMT</div>

                    <div className={Styles.inputWrap}>
                        <div className={Styles.inputTitle}>{t("Сумма")}</div>
                        <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*[.,]?[0-9]*"
                            value={amount}
                            onChange={onAmountChange}
                            placeholder={t("Введите сумму")}
                        />

                        {/* ✅ hints */}
                        {!isDecimalsReady && (
                            <div className={Styles.decimalsHint}>{t("Загрузка...")}</div>
                        )}

                        {isDecimalsReady && !!address && (
                            <div className={Styles.decimalsHint}>
                                {isBalanceLoading ? (
                                    t("Загрузка...")
                                ) : (
                                    <>
                                        {t("Доступно")}: <span>{balanceHuman ?? "—"} OMT</span>
                                    </>
                                )}
                            </div>
                        )}
                        <div className={`${Styles.statPos}`}>
                            {isDecimalsReady &&
                                !isBalanceLoading &&
                                isAmountPositive &&
                                balance != null &&
                                amountWei != null &&
                                !hasEnoughBalance && (
                                    <div className={`${Styles.decimalsHint} ${Styles.fHeight}`}>
                                        {t("Недостаточно средств для заморозки!")}
                                    </div>
                                )}
                        </div>

                    </div>

                    <div className={Styles.periodWrap}>
                        <div className={Styles.periodTitle}>{t("Выберите период заморозки")}</div>
                        <div className={Styles.periodBox}>
                            {tokenFreezeData?.periods?.map((item, index) => (
                                <div
                                    className={`${Styles.periodItem} ${
                                        item.id === currentPeriodId ? Styles.periodItemActive : ""
                                    }`}
                                    key={"period" + index}
                                    onClick={() => {
                                        setCurrentPeriodId(item.id);
                                    }}
                                >
                                    {item.days} {t("дней")}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div
                        className={`${Styles.modalContentWrapMainBtn} ${
                            isButtonDisabled ? Styles.modalContentWrapMainBtnDsb : ""
                        }`}
                        onClick={isButtonDisabled ? undefined : mainBtnHandler}
                        role="button"
                    >
                        {isConfirming ? t("Загрузка...") : t("Заморозить")}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default LaunchpadStakeModal;













